<template>
  <v-form @submit.prevent="applyFilters">
    <div
      v-for="filter in filterConfig"
      :key="filter.value"
      class="mb-4"
    >
      <!--component
        :is="filter.component"
        v-model="form[filter.value]"
        v-bind="getProps(filter)"
      /-->
      <component
        :is="filter.component"
        v-model="form[filter.value]"
        v-bind="getProps(filter)"
        variant="solo-filled"
      />
    </div>

    <v-btn type="submit" color="primary" class="mr-2">Aplicar filtros</v-btn>
    <v-btn variant="text" color="red" @click="resetFilters">Limpiar</v-btn>
  </v-form>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import debounce from 'lodash.debounce';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useLayoutField } from '@/composables/useLayoutField';
import { configRegistry } from '@/lib/configRegistry';
import { getListPreset, resolveViewName } from '@/lib/presets';

interface FilterOption {
  value: string;
  label: string;
  component: any; // Directly use Vuetify component
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  numeric?: boolean;
  options?: any[];
  itemText?: string;
  itemValue?: string;
  fetchOptions?: keyof typeof remoteOptionsRegistry;
  range?: boolean;
  transform?:any;
  searchValue?:any;
  fetchParams?:any;
  multiple?: boolean;
  defaultValue?: any;
}

const props = defineProps<{
  moduleName:string
}>();


const filterConfig = ref<FilterOption[]>([]);
const form = ref<Record<string, any>>({});
const searchInputs = ref<Record<string, string>>({});
const loadingStates = ref<Record<string, boolean>>({});
const debouncedFetchers = ref<Record<string, (query: string) => void>>({});

const anyLoading = computed(() => Object.values(loadingStates.value).some(v => v));
const route = useRoute();

const getProps = (filter: FilterOption) => {
  const key = filter.value;

  const props: Record<string, any> = {
    label: filter.label,
    placeholder: filter.placeholder ?? '',
    type: filter.numeric ? 'number' : 'text',
    clearable: true,
    density: 'compact',
    range: filter.range,
    multiple: filter.multiple,
    loading: !!loadingStates.value[key], // 👈 loader reactivo
  };

  if (filter.prefix) props.prefix = filter.prefix;
  if (filter.suffix) props.suffix = filter.suffix;

  // Static options
  if (filter.options) {
    props.items = filter.options;
    props['item-title'] = filter.itemText || 'text';
    props['item-value'] = filter.itemValue || 'value';
  }

  // Dynamic options (fetchOptions)
  if (filter.fetchOptions) {
    const fetchFn = remoteOptionsRegistry[filter.fetchOptions];

    // Debounced fetcher para búsqueda
    if (!debouncedFetchers.value[key]) {
      debouncedFetchers.value[key] = debounce(async (val: string) => {
        loadingStates.value[key] = true;
        let params: Record<string, any> = {};
        if (filter.searchValue && val) {
          params[`filter[${filter.searchValue}]`] = val;
        }
        if (typeof filter.fetchParams === 'function') {
          params = { ...params, ...filter.fetchParams(form.value) };
        }
        try {
          const results = await fetchFn(params);
          filter.options = Array.isArray(results) ? results : [];
          props.items = filter.options;
        } catch (err) {
          console.error(`❌ Error buscando "${filter.value}" con "${val}"`, err);
        } finally {
          loadingStates.value[key] = false;
        }
      }, 400);
    }

    // Cuando busca el usuario
    props['onUpdate:search'] = (val: string) => {
      searchInputs.value[key] = val;
      debouncedFetchers.value[key](val);
    };

    // Cuando se abre el dropdown
    props['onFocus'] = (open: boolean) => {
      if (
        open &&
        (!filter.options || filter.options.length === 0)
      ) {
        loadingStates.value[key] = true;
        let params: Record<string, any> = {};
        if (typeof filter.fetchParams === 'function') {
          params = { ...params, ...filter.fetchParams(form.value) };
        }
        fetchFn(params)
          .then(results => {
            filter.options = Array.isArray(results) ? results : [];
            props.items = filter.options;
          })
          .catch(err => {
            console.error(`❌ Error buscando "${filter.value}" al abrir`, err);
          })
          .finally(() => {
            loadingStates.value[key] = false;
          });
      }
    };

    // items (reactivo)
    props.items = filter.options || [];
    props['item-title'] = filter.itemText || 'text';
    props['item-value'] = filter.itemValue || 'value';
  }

  return props;
};


const moduleName = props.moduleName;

const filters = useLayoutField(moduleName, 'filters');
const filtersRecords = useLayoutField(moduleName, 'filters_records');

onMounted(async () => {
  const loader = configRegistry[moduleName];

  if (!loader) {
    console.error(`[Filters] No se encontró config para: "${moduleName}"`);
    return;
  }

  try {
    const mod = await loader();
    const viewName = resolveViewName(moduleName, route, mod);
    const preset = getListPreset(mod, viewName);
    filterConfig.value = preset.filters || mod.FILTERS || mod.filters || [];
  } catch (err) {
    console.error(`[Filters] Error cargando config "${moduleName}"`, err);
  }

  filterConfig.value.forEach((f) => {
    if (filtersRecords.value?.[f.value]) {
      form.value[f.value] = filtersRecords.value[f.value];
    } else if (filters.value?.[f.value] !== undefined) {
      form.value[f.value] = filters.value[f.value];
    } else if (f.defaultValue !== undefined) {
      form.value[f.value] = f.defaultValue;
    } else {
      form.value[f.value] = f.multiple ? [] : null;
    }
  });

  // --- 👇 Esto es el fix clave ---
  filterConfig.value.forEach((f) => {
    if (f.component?.name === 'VAutocomplete' && form.value[f.value]) {
      // Si es múltiple y es un array, rehidrata todas las opciones
      if (f.multiple && Array.isArray(form.value[f.value])) {
        f.options = [...form.value[f.value]];
      }
      // Si es simple (objeto, no array)
      else if (typeof form.value[f.value] === 'object' && !Array.isArray(form.value[f.value])) {
        f.options = [form.value[f.value]];
      }
    }
  });

});

const applyFilters = () => {
  const clean: Record<string, any> = {};
  const filterRecords: Record<string, any> = {};
  const filterKeys = new Set(filterConfig.value.map((f) => f.value));

  filterConfig.value.forEach(filter => {
    const val = form.value[filter.value];
    if (
      val !== null &&
      val !== undefined &&
      (Array.isArray(val) ? val.length > 0 : val !== '')
    ) {
      if (typeof filter?.transform === 'function') {
        // Si es multiple, pasa el array completo al transform y deja que el transform decida
        const transformed = filter.transform(val, form.value);
        if (typeof transformed === 'object' && !Array.isArray(transformed)) {
          Object.assign(clean, transformed);
        } else {
          clean[filter.value] = transformed;
        }
      } else {
        clean[filter.value] = val;
      }
      // Si es multiple, guarda el array, si no, el valor
      if (filter.component.name === 'VAutocomplete') {
        filterRecords[filter.value] = val;
      }
    }
  });

  // Preserve contextual filters not managed by this drawer (e.g. selected tab key).
  const preservedContext = Object.entries(filters.value || {}).reduce(
    (acc, [key, value]) => {
      if (!filterKeys.has(key)) acc[key] = value;
      return acc;
    },
    {} as Record<string, any>
  );

  filters.value = { ...preservedContext, ...clean };
  filtersRecords.value = filterRecords;
};



const resetFilters = () => {
  const filterKeys = new Set(filterConfig.value.map((f) => f.value));
  const preservedContext = Object.entries(filters.value || {}).reduce(
    (acc, [key, value]) => {
      if (!filterKeys.has(key)) acc[key] = value;
      return acc;
    },
    {} as Record<string, any>
  );

  const defaults: Record<string, any> = {};
  filterConfig.value.forEach((filter) => {
    form.value[filter.value] = filter.defaultValue !== undefined
      ? filter.defaultValue
      : (filter.multiple ? [] : null);

    if (filter.defaultValue !== undefined) {
      defaults[filter.value] = filter.defaultValue;
    }
  });

  filters.value = { ...preservedContext, ...defaults };
  filtersRecords.value = {};
};
</script>

<script lang="ts">
export default {
  name: 'Filters',
};
</script>
